import Input from "@components/ui/Input";

/**
 * What each PMS needs before it can be called.
 *
 * Mirrors the backend's Clinic.has_scheduling, which asks the provider which of
 * its settings are load-bearing rather than assuming NexHealth's. Keeping the
 * shape here means adding a PMS is one entry, not a new form.
 */
export const PMS_PROVIDERS = {
  nexhealth: {
    label: "NexHealth",
    blurb: "Live availability and booking through the NexHealth API.",
    fields: [
      {
        key: "subdomain",
        label: "NexHealth Subdomain",
        placeholder: "e.g., my-practice",
        required: true,
      },
      {
        key: "location_id",
        label: "NexHealth Location ID",
        placeholder: "e.g., 12345",
        required: true,
      },
    ],
  },
  fathom: {
    label: "Fathom",
    blurb:
      "Read-only replication through Harbor. Availability is computed from opening hours minus booked appointments, so this clinic needs opening hours set.",
    fields: [
      {
        key: "harbor_clinic_id",
        label: "Harbor Clinic ID",
        placeholder: "e.g., smile-point",
        required: true,
      },
      {
        key: "api_key",
        label: "Harbor API Key",
        placeholder: "ek_...",
        required: true,
        secret: true,
      },
      {
        key: "base_url",
        label: "Harbor Base URL",
        placeholder: "Leave blank to use the default deployment",
        required: false,
        hint: "Only set this for a clinic served by a different Harbor.",
      },
    ],
  },
};

/** Every key any provider's form owns, so a switch can clear the others. */
const OWNED_KEYS = new Set(
  Object.values(PMS_PROVIDERS).flatMap((p) => p.fields.map((f) => f.key))
);

/** A clinic is connected when its provider's required settings are all present. */
export function isPmsConfigured(provider, config) {
  const spec = PMS_PROVIDERS[provider];
  if (!spec) return false;
  return spec.fields
    .filter((f) => f.required)
    .every((f) => String(config?.[f.key] ?? "").trim() !== "");
}

function StatusPill({ connected }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        connected ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"
      }`}
    >
      {connected ? "Connected" : "Not connected"}
    </span>
  );
}

export default function SchedulingEditor({ provider, config, onChange }) {
  const spec = PMS_PROVIDERS[provider];
  const connected = isPmsConfigured(provider, config);

  /**
   * Write one setting, preserving everything this form does not own.
   *
   * The API replaces pms_config wholesale, and the sync writes runtime state
   * into the same blob (the operatory list, the appointment freshness stamp).
   * Spreading the existing config keeps an admin's edit from wiping it.
   */
  const setSetting = (key, value) => {
    const next = { ...(config || {}) };
    if (String(value).trim() === "") {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange("pms_config", next);
  };

  /**
   * Switching provider drops the previous provider's settings but keeps
   * anything neither form owns, so unrelated state survives. Leaving them
   * behind would make the clinic list show, say, a NexHealth subdomain against
   * a Fathom clinic.
   */
  const setProvider = (nextProvider) => {
    const kept = Object.fromEntries(
      Object.entries(config || {}).filter(([k]) => !OWNED_KEYS.has(k))
    );
    onChange("active_pms_provider", nextProvider || null);
    onChange("pms_config", Object.keys(kept).length ? kept : null);
  };

  return (
    <div className="border rounded-lg p-4 bg-white space-y-3">
      <div>
        <div className="text-sm font-semibold flex items-center gap-2">
          PMS Integration
          <StatusPill connected={connected} />
        </div>
        <div className="text-xs text-gray-600 mt-1">
          {spec
            ? spec.blurb
            : "Connect this clinic to a practice management system to sync its schedule."}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Provider</label>
        <select
          value={provider ?? ""}
          onChange={(e) => setProvider(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition bg-white"
        >
          <option value="">Not connected</option>
          {Object.entries(PMS_PROVIDERS).map(([key, p]) => (
            <option key={key} value={key}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      {spec?.fields.map((f) => (
        <div key={f.key}>
          <Input
            label={f.required ? f.label : `${f.label} (optional)`}
            type={f.secret ? "password" : "text"}
            value={config?.[f.key] ?? ""}
            onChange={(e) => setSetting(f.key, e.target.value)}
            placeholder={f.placeholder}
            autoComplete={f.secret ? "new-password" : undefined}
          />
          {f.hint && <div className="text-xs text-gray-500 mt-1">{f.hint}</div>}
        </div>
      ))}

      {provider === "fathom" && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          Fathom is read-only for now: the schedule syncs in, but bookings are
          not written back to the clinic's system.
        </div>
      )}
    </div>
  );
}
